// Add a supplier quote to one inquiry item via
// POST ums/v1/cms/restaurant-inquiries/:id/items/:itemId/quotes.
// Picker flow per the quotation-source discovery APIs (#2146):
//   (1) search /quotation-suppliers → pick a supplier
//   (2) ProductMatcher over /quotation-suppliers/:supplierId/products — the
//       supplier's public catalog (deal-backed rows flagged), with ranked
//       suggestions and inline product creation
//   (3) that product's id is the productId for POST .../quotes.
// The backend re-derives supplierId from productId; the supplier select here is
// purely to find the SKU.

import {
  Box,
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@elkaso-app/web-design';
import type { QuotationSupplierProduct } from 'apis/restaurant-inquiries';
import {
  QuotationSupplierOption,
  RestaurantInquiryItem,
  useCreateInquiryItemQuoteApi,
  useSearchQuotationSuppliersApi,
} from 'apis/restaurant-inquiries';
import { ProductQuoteHistoryModal } from 'pages/inquiry-processor/components/product-quote-history';
import { PriceLadder } from 'pages/inquiry-processor/components/quote-matcher/price-ladder';
import { ProductMatcher } from 'pages/inquiry-processor/components/quote-matcher/product-matcher';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { ladderOf, resolveTier, type PriceTier } from 'pages/inquiry-processor/utils/price-units';
import { useState } from 'react';
import { FiClock } from 'react-icons/fi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  inquiryId: string;
  item: RestaurantInquiryItem;
}

export const AddItemQuoteModal = ({ isOpen, onClose, inquiryId, item }: Props) => {
  const toast = useToast();
  const { loadOptions } = useSearchQuotationSuppliersApi();
  const createQuote = useCreateInquiryItemQuoteApi();

  const [supplier, setSupplier] = useState<QuotationSupplierOption | null>(null);
  const [product, setProduct] = useState<QuotationSupplierProduct | null>(null);
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  // The operator's unit pick; null until they choose, so the effective rung
  // below re-resolves whenever the product (and its ladder) changes.
  const [pickedUnit, setPickedUnit] = useState<PriceTier | null>(null);

  // Which rung the entered price is agreed against — the pick when it still
  // names a rung this product defines, else inferred from its defaultUnit.
  const effectiveUnit = product ? resolveTier(ladderOf(product), pickedUnit, product.defaultUnit) : null;

  const pickProduct = (p: QuotationSupplierProduct | null) => {
    setProduct(p);
    setPickedUnit(null);
    // Prefill the price from the deal (fallback: public price); editable — an
    // inactive deal is already flagged on the product row.
    const suggested = p?.exclusiveDeals?.[0]?.dealPrice ?? p?.defaultPrice;
    setPrice(p && suggested != null ? String(suggested) : '');
  };

  const reset = () => {
    setSupplier(null);
    setProduct(null);
    setPrice('');
    setNotes('');
    setPickedUnit(null);
  };

  const save = async () => {
    if (!product) {
      toast({ title: 'Please pick a supplier product', status: 'warning' });
      return;
    }
    if (!(Number(price) > 0)) {
      toast({ title: 'Enter a price greater than 0', status: 'warning' });
      return;
    }
    try {
      await createQuote.mutateAsync({
        id: inquiryId,
        itemId: item.id,
        productId: product.id,
        price: Number(price),
        currency: 'AED',
        priceUnit: effectiveUnit ?? undefined,
        notes: notes.trim() || undefined,
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      toast({
        title: 'Failed to add quote',
        description: status === 409 ? 'This product has already been quoted on this item.' : apiErrorMessage(err),
        status: 'error',
      });
      return;
    }
    toast({ title: 'Quote added', status: 'success' });
    reset();
    onClose();
  };

  const supplierMeta = supplier?.raw;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '2xl' }}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Quote — {item.masterProduct?.name || `Item #${item.id}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired mb='4'>
            <FormLabel fontSize='sm'>Supplier</FormLabel>
            <ChakraAsyncSelect
              loadOptions={loadOptions}
              defaultOptions
              value={supplier}
              onChange={(opt: any) => {
                setSupplier(opt || null);
                pickProduct(null); // products are supplier-specific
              }}
              isClearable
              placeholder='Search quotation suppliers…'
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
            />
            {supplierMeta && (
              <Text fontSize='xs' color='gray.500' mt='1'>
                {[
                  supplierMeta.category,
                  supplierMeta.moq && `MOQ: ${supplierMeta.moq}`,
                  supplierMeta.delivery && `Delivery: ${supplierMeta.delivery}`,
                  supplierMeta.paymentTerms && `Terms: ${supplierMeta.paymentTerms}`,
                ]
                  .filter(Boolean)
                  .join('  |  ')}
              </Text>
            )}
          </FormControl>
          <FormControl isRequired mb='4'>
            <FormLabel fontSize='sm'>Supplier product</FormLabel>
            <ProductMatcher
              supplierId={supplier?.value}
              supplierName={supplier?.label}
              item={item}
              value={product}
              onChange={pickProduct}
            />
          </FormControl>
          {product && (
            <Box mb='4'>
              <PriceLadder
                product={product}
                price={price}
                onPriceChange={setPrice}
                priceUnit={effectiveUnit}
                onPriceUnitChange={setPickedUnit}
                targetPrice={item.expectedPrice}
                extra={
                  <ProductQuoteHistoryModal
                    productId={product.id}
                    productName={product.nameEn || `Product #${product.id}`}
                    excludeInquiryId={/^\d+$/.test(inquiryId) ? Number(inquiryId) : undefined}
                    trigger={
                      <IconButton
                        aria-label='Quote history'
                        title='Quote history'
                        size='sm'
                        variant='ghost'
                        icon={<FiClock />}
                      />
                    }
                  />
                }
              />
            </Box>
          )}
          <FormControl>
            <FormLabel fontSize='sm'>Notes</FormLabel>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder='e.g. Best available price this week'
            />
          </FormControl>
          <Text fontSize='xs' color='gray.500' mt='3'>
            The quote is created as PENDING against the selected supplier SKU.
          </Text>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' onClick={onClose} isDisabled={createQuote.isLoading}>
            Cancel
          </Button>
          <Button colorScheme='red' onClick={save} isLoading={createQuote.isLoading} loadingText='Adding…'>
            Add Quote
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
